import React from 'react';
import { UserRound, CheckCircle, Sparkles, Zap } from 'lucide-react';

const CourseProgressSection = ({
  user,
  studentPath,
  pathCourses,
  userProgress,
  pathProgress,
  currentStep,
  totalSteps,
  userXP = null,
  onStartCourse,
  progressLoading = false
}) => {
  // Count completed courses (including both lessons and submission forms)
  const getCompletedCoursesCount = () => {
    if (!pathCourses || !pathCourses.length) return 0;

    if (userProgress?.isPathCompleted) {
      return pathCourses.length;
    }

    let completedCoursesCount = 0;

    for (const course of pathCourses) {
      const courseContent = [];

      if (course.lessons && course.lessons.length > 0) {
        course.lessons.forEach(lesson => {
          courseContent.push({ ...lesson, type: 'lesson' });
        });
      }

      if (course.submissionForms && course.submissionForms.length > 0) {
        course.submissionForms.forEach(form => {
          courseContent.push({ ...form, type: 'submissionForm' });
        });
      }

      const totalContent = courseContent.length;
      if (totalContent === 0) continue;

      let completedContent = 0;

      if (userProgress?.completedLessons) {
        const completedLessonIds = userProgress.completedLessons.map(cl => {
          try {
            if (typeof cl.lesson === 'object' && cl.lesson && cl.lesson._id) {
              return cl.lesson._id;
            }
            return cl.lesson;
          } catch (error) {
            return null;
          }
        }).filter(id => id !== null);

        courseContent.forEach(item => {
          if (item.type === 'lesson' && completedLessonIds.includes(item._id)) {
            completedContent++;
          }
        });
      }

      if (userProgress?.completedSubmissionForms) {
        const completedFormIds = userProgress.completedSubmissionForms.map(cf => {
          try {
            if (typeof cf.submissionForm === 'object' && cf.submissionForm && cf.submissionForm._id) {
              return cf.submissionForm._id;
            }
            return cf.submissionForm;
          } catch (error) {
            return null;
          }
        }).filter(id => id !== null);

        courseContent.forEach(item => {
          if (item.type === 'submissionForm' && completedFormIds.includes(item._id)) {
            completedContent++;
          }
        });
      }

      if (completedContent === totalContent && totalContent > 0) {
        completedCoursesCount++;
      }
    }

    return completedCoursesCount;
  };

  const isCompleted = userProgress?.isPathCompleted || pathProgress >= 100;
  const firstName = user?.name?.split(' ')[0] || 'Student';

  return (
    <>
      {/* Top Bar */}
      <div className="border-b border-border bg-white px-4 sm:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-primary-foreground" />
          </div>
          <div className="leading-tight">
            <p className="font-semibold text-sm text-foreground">{studentPath?.title || 'Learning Path'}</p>
            <p className="text-xs text-muted-foreground">{user?.group?.title || ''}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* XP Badge */}
          <span className="flex items-center gap-1.5 text-sm font-semibold text-xp">
            <Zap className="w-4 h-4" />
            {(userXP?.totalXP || 0).toLocaleString()} XP
          </span>

          {/* Avatar Button */}
          <button className="w-9 h-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors overflow-hidden">
            {user?.profilePicture?.url || user?.profilePicture?.data ? (
              <img
                src={user.profilePicture.url || `data:${user.profilePicture.contentType};base64,${user.profilePicture.data}`}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <UserRound className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-white px-4 sm:px-6 pt-8 sm:pt-10 pb-6 sm:pb-8">
        <h1 className="text-2xl sm:text-4xl font-bold text-foreground mb-1">
          You're {pathProgress}% through your journey, {firstName}.
        </h1>
        <p className="text-muted-foreground text-sm sm:text-base mb-6">
          {isCompleted
            ? 'Goal Completed · Amazing work!'
            : `Course ${currentStep} of ${totalSteps} · Keep the streak alive`}
        </p>

        {/* Progress Bar */}
        <div className="relative mb-2">
          <div className="w-full bg-muted rounded-full h-2.5 overflow-visible">
            <div
              className="bg-primary h-2.5 rounded-full transition-all duration-700 ease-out relative"
              style={{ width: `${Math.min(pathProgress, 100)}%` }}
            >
              {pathProgress > 0 && !isCompleted && (
                <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-4 h-4 bg-primary rounded-full border-2 border-white shadow-sm" />
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-between text-sm text-muted-foreground">
          <span>{pathProgress}%</span>
          {isCompleted ? (
            <span className="text-success font-medium flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5" />
              Goal Completed
            </span>
          ) : (
            <span>{currentStep}/{totalSteps} courses</span>
          )}
        </div>

        {/* Start / Jump Back In CTA */}
        {!isCompleted && onStartCourse && (
          <div className="mt-5">
            <button
              onClick={onStartCourse}
              disabled={progressLoading}
              className="px-6 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {progressLoading
                ? 'Loading...'
                : userProgress?.currentContent
                  ? 'Jump Back In'
                  : 'Start Course'}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CourseProgressSection;
